import React from 'react';
import { useParams } from 'react-router-dom';
import { useSubTask } from '../hooks/useSubTask';
import { TopbarSearch } from '@/components/ui/TopbarSearch';
import { TablePagination } from '@/components/ui/TablePagination';
import { ActionMenu } from '@/components/ui/ActionMenu';
import { DonutLegendChart } from '@/components/ui/DonutLegendChart';
import { cn } from '@/utils/cn';

export function SubTasksListPage() {
  const { id = 'default-id' } = useParams();
  const { info, items, stats, isLoading, search, setSearch, tab, setTab } = useSubTask(id);

  if (isLoading) return <div className="p-10 text-center animate-pulse text-slate-400">Đang tải dữ liệu...</div>;

  return (
    <div className="mx-auto max-w-screen-2xl space-y-4 pb-10">
      {/* Breadcrumb & Title Section */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2 sm:text-sm text-xs text-slate-400 tracking-wider">
             <span>Công việc</span>
             <i className="ri-arrow-right-s-line" />
             <span>Chi tiết công việc</span>
             <i className="ri-arrow-right-s-line" />
             <span className="text-slate-900">Công việc con</span>
          </div>
          <h1 className="text-2xl text-slate-900 tracking-tight">Công việc con</h1>
          <p className="sm:text-sm text-xs text-slate-500">Danh sách các công việc con thuộc công việc chính.</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="h-10 rounded-md border border-slate-200 bg-white px-4 sm:text-sm text-xs text-slate-600 hover:bg-slate-50 transition-all flex items-center gap-2">
            <i className="ri-file-excel-line text-emerald-600" />
            Xuất Excel
          </button>
          <button className="h-10 rounded-md bg-emerald-800 px-6 sm:text-sm text-xs text-white hover:bg-emerald-900 transition-all flex items-center gap-2">
            <i className="ri-add-line text-lg" />
            Thêm công việc con
          </button>
        </div>
      </div>

      {/* Main Header Info Card */}
      <div className="rounded-md bg-white p-3 border border-slate-100 flex flex-col lg:flex-row gap-6">
         <div className="flex items-center gap-4 flex-1">
            <div className="h-16 w-16 rounded-md bg-emerald-50 flex items-center justify-center text-emerald-600 border border-emerald-100">
               <i className="ri-leaf-line text-3xl" />
            </div>
            <div className="space-y-1">
               <div className="flex items-center gap-2">
                  <h2 className="text-lg text-slate-900 leading-tight">{info?.title}</h2>
                  <span className="inline-flex items-center rounded-md border border-blue-100 bg-blue-50 px-2 py-0.5 sm:text-sm text-xs text-blue-600 tracking-tight">
                    {info?.type === 'construction' ? 'Thi công' : 'Bảo dưỡng'}
                  </span>
               </div>
               <p className="sm:text-sm text-xs text-slate-500">{info?.location} • {info?.code}</p>
            </div>
         </div>
         
         <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-4 lg:border-l border-slate-50 lg:pl-8">
            <div className="space-y-1">
               <p className="sm:text-sm text-xs text-slate-400 tracking-wider">Trạng thái</p>
               <span className="inline-flex items-center rounded-md bg-blue-50 text-blue-600 border border-blue-100 px-2 py-0.5 sm:text-sm text-xs">Đang thực hiện</span>
            </div>
            <div className="space-y-1">
               <p className="sm:text-sm text-xs text-slate-400 tracking-wider">Tiến độ tổng thể</p>
               <div className="flex items-center gap-2">
                  <span className="sm:text-sm text-xs text-slate-900">{info?.total_progress}%</span>
                  <div className="h-1.5 w-24 rounded-full bg-slate-100 overflow-hidden">
                     <div className="h-full bg-emerald-500 w-[60%]" />
                  </div>
                  <span className="sm:text-sm text-xs text-slate-400">{info?.completed_count}/{info?.total_count} công việc</span>
               </div>
            </div>
            <div className="space-y-1">
               <p className="sm:text-sm text-xs text-slate-400 tracking-wider">Người phụ trách</p>
               <div className="flex items-center gap-2">
                  <div className="h-6 w-6 rounded-full bg-slate-100 border border-slate-200 overflow-hidden">
                     <img src="https://ui-avatars.com/api/?name=Tran+Minh+Tuan&background=random" className="h-full w-full" />
                  </div>
                  <span className="sm:text-sm text-xs text-slate-700">{info?.manager_name}</span>
               </div>
            </div>
            <div className="space-y-1">
               <p className="sm:text-sm text-xs text-slate-400 tracking-wider">Hạn hoàn thành</p>
               <div className="flex items-center gap-1.5 text-slate-700">
                  <i className="ri-calendar-line text-slate-400" />
                  <span className="sm:text-sm text-xs">30/05/2024</span>
               </div>
            </div>
         </div>
      </div>

      {/* Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left Column: List & Filters */}
        <div className="lg:col-span-9 space-y-4">
           {/* Tabs */}
           <div className="flex items-center gap-6 border-b border-slate-100 px-2">
              <button 
                onClick={() => setTab('list')}
                className={cn("pb-2 sm:text-sm text-xs transition-all border-b-2", tab === 'list' ? "text-emerald-600 border-emerald-700" : "text-slate-400 border-transparent")}
              >
                Danh sách ({stats.total})
              </button>
              <button 
                onClick={() => setTab('trash')}
                className={cn("pb-2 sm:text-sm text-xs transition-all border-b-2", tab === 'trash' ? "text-emerald-600 border-emerald-700" : "text-slate-400 border-transparent")}
              >
                Thùng rác (2)
              </button>
           </div>

           {/* Toolbar */}
           <div className="flex flex-col gap-3 md:flex-row md:items-center bg-white p-3 rounded-md border border-slate-50">
              <div className="flex-1 max-w-sm">
                 <TopbarSearch placeholder="Tìm kiếm công việc con..." value={search} onSearch={setSearch} />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                 <select className="h-9 rounded-md border border-slate-200 bg-slate-50 px-3 sm:text-sm text-xs text-slate-600 outline-none">
                    <option>Tất cả trạng thái</option>
                 </select>
                 <select className="h-9 rounded-md border border-slate-200 bg-slate-50 px-3 sm:text-sm text-xs text-slate-600 outline-none">
                    <option>Tất cả người phụ trách</option>
                 </select>
                 <select className="h-9 rounded-md border border-slate-200 bg-slate-50 px-3 sm:text-sm text-xs text-slate-600 outline-none">
                    <option>Tất cả mức độ ưu tiên</option>
                 </select>
                 <button className="h-9 rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs text-slate-600 flex items-center gap-1.5 hover:bg-slate-50 transition-all">
                    <i className="ri-filter-3-line" />
                    Bộ lọc
                 </button>
                 <div className="h-9 flex items-center bg-slate-100 rounded-md p-1 ml-2">
                    <button className="h-7 w-7 rounded bg-white flex items-center justify-center text-slate-600"><i className="ri-list-check" /></button>
                    <button className="h-7 w-7 flex items-center justify-center text-slate-400"><i className="ri-grid-fill" /></button>
                 </div>
              </div>
           </div>

           {/* Table */}
           <div className="rounded-md bg-white border border-slate-100 overflow-hidden">
             <table className="w-full border-collapse text-left sm:text-sm text-xs">
                <thead>
                  <tr className="border-b border-slate-50 bg-slate-50/50">
                    <th className="p-3 w-10 text-center font-normal"><input type="checkbox" className="rounded border-slate-300" /></th>
                    <th className="p-3 text-slate-500 tracking-tight text-center w-12 font-normal">STT</th>
                    <th className="p-3 text-slate-500 tracking-tight font-normal">Tên công việc con</th>
                    <th className="p-3 text-slate-500 tracking-tight font-normal">Người phụ trách</th>
                    <th className="p-3 text-slate-500 tracking-tight font-normal">Hạn hoàn thành</th>
                    <th className="p-3 text-slate-500 tracking-tight text-center font-normal">Trạng thái</th>
                    <th className="p-3 text-slate-500 tracking-tight text-center font-normal">Ưu tiên</th>
                    <th className="p-3 text-slate-500 tracking-tight font-normal">Tiến độ</th>
                    <th className="p-3 w-10 font-normal"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                   {items.map((task, idx) => (
                     <tr key={task.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="p-4 text-center"><input type="checkbox" className="rounded border-slate-300" /></td>
                        <td className="p-4 text-center text-slate-400">{idx + 1}</td>
                        <td className="p-3 text-slate-800">{task.name}</td>
                        <td className="p-3">
                           <div className="flex items-center gap-2">
                              <div className="h-6 w-6 rounded-full bg-slate-100 border border-slate-200 overflow-hidden">
                                 <img src={task.assigned_to?.avatar_path || "https://ui-avatars.com/api/?name=User&background=random"} className="h-full w-full" />
                              </div>
                              <span className="sm:text-sm text-xs text-slate-700">{task.assigned_to?.full_name}</span>
                           </div>
                        </td>
                        <td className="p-4 text-slate-600">{new Date(task.due_date).toLocaleDateString('vi-VN')}</td>
                        <td className="p-4 text-center">
                           <span className={cn(
                             "px-2 py-0.5 rounded-md border sm:text-sm text-xs",
                             task.status_code === 'completed' ? "bg-emerald-50 text-emerald-600 border-emerald-100" :
                             task.status_code === 'in_progress' ? "bg-blue-50 text-blue-600 border-blue-100" :
                             "bg-slate-50 text-slate-500 border-slate-100"
                           )}>
                             {task.status_code === 'completed' ? 'Hoàn thành' : task.status_code === 'in_progress' ? 'Đang thực hiện' : 'Chưa bắt đầu'}
                           </span>
                        </td>
                        <td className="p-4 text-center">
                           <span className={cn(
                             "px-2 py-0.5 rounded-md sm:text-sm text-xs",
                             task.priority_code === 'high' ? "bg-rose-50 text-rose-600" :
                             task.priority_code === 'medium' ? "bg-amber-50 text-amber-600" :
                             "bg-slate-100 text-slate-500"
                           )}>
                             {task.priority_code === 'high' ? 'Cao' : task.priority_code === 'medium' ? 'Trung bình' : 'Thấp'}
                           </span>
                        </td>
                        <td className="p-3">
                           <div className="flex items-center gap-2">
                              <span className="sm:text-sm text-xs text-slate-900 w-8">{task.progress}%</span>
                              <div className="h-1.5 flex-1 max-w-[80px] rounded-full bg-slate-100 overflow-hidden">
                                 <div className="h-full bg-emerald-500" style={{ width: `${task.progress}%` }} />
                              </div>
                           </div>
                        </td>
                        <td className="p-3 text-right">
                           <ActionMenu actions={[{ label: 'Xem chi tiết', icon: 'ri-eye-line', onClick: () => {} }]} />
                        </td>
                     </tr>
                   ))}
                </tbody>
             </table>
             <div className="p-3 border-t border-slate-50 flex items-center justify-between">
                <span className="sm:text-sm text-xs text-slate-400">Hiển thị 1 - {items.length} trong tổng số {stats.total} công việc con</span>
                <TablePagination totalItems={stats.total} pageSize={10} currentPage={1} onPageChange={() => {}} />
             </div>
           </div>
        </div>

        {/* Right Column: Stats & Sidebar */}
        <div className="lg:col-span-3 space-y-4">
           {/* Progress Chart Card */}
           <div className="rounded-md bg-white p-3 border border-slate-100 space-y-4">
              <h3 className="sm:text-sm text-xs text-slate-400 tracking-wider border-b border-slate-50 pb-2">Tiến độ công việc con</h3>
              <DonutLegendChart 
                total={stats.total}
                data={[
                  { label: 'Hoàn thành', value: stats.completed, color: '#10b981', percentage: Math.round((stats.completed/stats.total)*100) },
                  { label: 'Đang thực hiện', value: stats.inProgress, color: '#3b82f6', percentage: Math.round((stats.inProgress/stats.total)*100) },
                  { label: 'Chưa bắt đầu', value: stats.pending, color: '#f59e0b', percentage: Math.round((stats.pending/stats.total)*100) },
                  { label: 'Quá hạn', value: stats.overdue, color: '#ef4444', percentage: Math.round((stats.overdue/stats.total)*100) },
                ]}
              />
           </div>

           {/* Status Stats List */}
           <div className="rounded-md bg-white p-3 border border-slate-100 space-y-4">
              <h3 className="sm:text-sm text-xs text-slate-400 tracking-wider border-b border-slate-50 pb-2">Thống kê theo trạng thái</h3>
              <div className="space-y-3">
                 {[
                   { label: 'Tổng số công việc con', value: stats.total, icon: 'ri-file-list-3-line', color: 'text-slate-400' },
                   { label: 'Hoàn thành', value: stats.completed, icon: 'ri-checkbox-circle-line', color: 'text-emerald-500' },
                   { label: 'Đang thực hiện', value: stats.inProgress, icon: 'ri-loader-4-line', color: 'text-blue-500' },
                   { label: 'Chưa bắt đầu', value: stats.pending, icon: 'ri-time-line', color: 'text-amber-500' },
                   { label: 'Quá hạn', value: stats.overdue, icon: 'ri-error-warning-line', color: 'text-rose-500' },
                 ].map((stat, i) => (
                   <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2 text-slate-600">
                         <i className={cn(stat.icon, stat.color, "text-sm")} />
                         <span>{stat.label}</span>
                      </div>
                      <span className="text-slate-900">{stat.value}</span>
                   </div>
                 ))}
              </div>
           </div>

           {/* Priority Stats List */}
           <div className="rounded-md bg-white p-3 border border-slate-100 space-y-4">
              <h3 className="sm:text-sm text-xs text-slate-400 tracking-wider border-b border-slate-50 pb-2">Thống kê theo ưu tiên</h3>
              <div className="space-y-3">
                 {[
                   { label: 'Cao', value: stats.priorityStats.high, color: 'bg-rose-500' },
                   { label: 'Trung bình', value: stats.priorityStats.medium, color: 'bg-amber-500' },
                   { label: 'Thấp', value: stats.priorityStats.low, color: 'bg-emerald-500' },
                 ].map((stat, i) => (
                   <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2 text-slate-600">
                         <div className={cn("h-2 w-2 rounded-full", stat.color)} />
                         <span>{stat.label}</span>
                      </div>
                      <span className="text-slate-900">{stat.value}</span>
                   </div>
                 ))}
              </div>
           </div>

           {/* Quick Actions */}
           <div className="rounded-md bg-white p-3 border border-slate-100 space-y-4">
              <h3 className="sm:text-sm text-xs text-slate-400 tracking-wider border-b border-slate-50 pb-2">Thao tác nhanh</h3>
              <div className="space-y-1">
                 <button className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-slate-50 sm:text-sm text-xs text-slate-700 transition-all">
                    <i className="ri-sort-asc text-slate-400" />
                    Sắp xếp thứ tự
                 </button>
                 <button className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-slate-50 sm:text-sm text-xs text-slate-700 transition-all">
                    <i className="ri-file-excel-line text-slate-400" />
                    Nhập từ Excel
                 </button>
                 <button className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-slate-50 sm:text-sm text-xs text-slate-700 transition-all">
                    <i className="ri-printer-line text-slate-400" />
                    In danh sách
                 </button>
              </div>
           </div>
        </div>
      </div>
    </div>
  );
}

export default SubTasksListPage;
